/********************************/
/*          About vuex          */
/********************************/
// The MIT License (MIT)
//
// Copyright (c) 2015-present Evan You
//
// Permission is hereby granted, free of charge, to any person obtaining a copy
// of this software and associated documentation files (the "Software"), to deal
// in the Software without restriction, including without limitation the rights
// to use, copy, modify, merge, publish, distribute, sublicense, and/or sell
// copies of the Software, and to permit persons to whom the Software is
// furnished to do so, subject to the following conditions:
//
// The above copyright notice and this permission notice shall be included in
// all copies or substantial portions of the Software.
//
// THE SOFTWARE IS PROVIDED "AS IS", WITHOUT WARRANTY OF ANY KIND, EXPRESS OR
// IMPLIED, INCLUDING BUT NOT LIMITED TO THE WARRANTIES OF MERCHANTABILITY,
// FITNESS FOR A PARTICULAR PURPOSE AND NONINFRINGEMENT. IN NO EVENT SHALL THE
// AUTHORS OR COPYRIGHT HOLDERS BE LIABLE FOR ANY CLAIM, DAMAGES OR OTHER
// LIABILITY, WHETHER IN AN ACTION OF CONTRACT, TORT OR OTHERWISE, ARISING FROM,
// OUT OF OR IN CONNECTION WITH THE SOFTWARE OR THE USE OR OTHER DEALINGS IN
// THE SOFTWARE.

import { mapState, mapActions } from 'vuex';
import config from '../../../../share/config';
import individualDiagnose from '../../../../share/util/individualDiagnose';
import individualWorkSupportUtil from '../../../../share/util/individualWorkSupportUtil';
import session from '../../../../share/session';
import error from '../../../../common/nodejs/errors';

/** 各フェーズ定義 */
const PHASE = {
  START: 'start',                            // 待機画面
  EXECUTION: 'execution',                    // 実行監視画面
  SUCCESS: 'success',                        // 停止画面
  ABORT_ERROR: 'abortError',                 // 異常終了表示画面
  CONDITION_ERROR: 'conditionError',         // 実施条件未成立表示画面
  DTC_DETECTION_ERROR: 'DTCdetectionError',  // DTC検出表示画面
  FAILED: 'failed'                           // 共通エラー画面
};

export default {
  name: 'requirement077',
  data() {
    return {
      phase: PHASE.START,                    // 画面フェーズ（初期値は待機画面）
      systemId: '',                          // システムID
      isLoading: false,                      // スピナー表示
      isDisabled : false,                    // 実行監視画面のキャンセルボタン押下フラグ
      modal_requirement_077: false
    };
  },

  props: {
    i18nWsKey: { type: String, default: '', required: true },
    workSupportId: { type: Number, default: '', required: true },
  },

  computed: {
    ...mapState({
      netAppId: state => state.netAppConnector.pairingAppId
    }),

    /** ダイアログのヘッダー色 */
    headerColor() {
      switch (this.phase) {
        case PHASE.ABORT_ERROR:
        case PHASE.CONDITION_ERROR:
        case PHASE.DTC_DETECTION_ERROR:
        case PHASE.FAILED:
          return 'danger';
        case PHASE.SUCCESS:
          return 'success';
        case PHASE.START:
        case PHASE.EXECUTION:
        default:
          return 'secondary';
      }
    }
  },

  methods: {
    ...mapActions('auth', ['logout', 'resetSession']),

    /** 初期化処理 */
    init() {
      this.modal_requirement_077 = true;
      this.systemId = individualDiagnose.getCurrentSystemId(this);
    },

    /** 終了処理 */
    async exitProcess() {
      try {
        this.isLoading = true;
        const token = session.getToken();
        await individualWorkSupportUtil.pollingWspApi(
          this, this.netAppId, this.systemId, this.workSupportId,
          {
            id_token: token.id_token,
            type: 'exit'
          },
          // エラー発生のコールバック
          (errorType) => { errorType; this.showUnexpectedError(); },
          // POST API 結果が異常の場合（handler.validate でエラーが発生した場合）のコールバック
          (result) => { this.validateErrorResult(result); },
          // GET API 結果が異常の場合（handler.validate でエラーが発生した場合）のコールバック
          (result) => { this.validateErrorResult(result); },
        );
        // DefaultSession遷移の結果に関係なくダイアログを閉じる
        this.closeWizard();
      }
      finally {
        this.isLoading = false;
      }
    },

    /** Wizardを閉じる */
    async closeWizard() {
      this.$emit('close-wizard');
      this.modal_requirement_077 = false;
    },

    /**
     * API 通信で共通バリデーションでエラーが発生した際のコールバック
     * @param {object} result バリデーション結果
     */
    validateErrorResult(result) {
      individualDiagnose.apiErrorDefaultAction(
        this, result,
        // ログアウト処理のコールバック
        (self) => { self; this.logout(this); },
        // セッションリセット処理のコールバック
        (self) => { self; this.resetSession(this); },
        // その他エラー発生のコールバック
        (result) => { result; this.showUnexpectedError(); },
      );
    },

    /**
     * 待機画面処理
     * status     = STATUS_WSP_COMPLETED : 停止画面
     * async_code = WSP_ABORT_ERROR      : 異常終了画面
     * async_code = WSP_CONDITION_ERROR  : 実施条件未成立表示画面
     * async_code = WSP_DETECT_DTC_ERROR : DTC検出エラー画面
     */
    async standby() {
      try {
        this.isLoading = true;

        // 実行状態に遷移
        this.phase = PHASE.EXECUTION;

        const token = session.getToken();
        const response = await individualWorkSupportUtil.pollingWspApi(
          this, this.netAppId, this.systemId, this.workSupportId,
          {
            id_token: token.id_token,
            type: 'standby'
          },
          // エラー発生のコールバックを実装
          // ここでは利用していないが、引数の errorType にはあらかじめ用意された定数文字列が返ってくる。
          (errorType) => { errorType; this.showUnexpectedError(); },
          // POST API 結果が異常の場合（handler.validate でエラーが発生した場合）のコールバックを定義する（validateErrorResult の中は同様のため、省略する）
          (result) => { this.validateErrorResult(result); },
          // GET API 結果が異常の場合（handler.validate でエラーが発生した場合）のコールバックを定義する（validateErrorResult の中は同様のため、省略する）
          (result) => { this.validateErrorResult(result); },
        );

        if (response) {
          const status = response.data.status;

          // executeが成功
          if (status === config.STATUS_WSP_COMPLETED) {
            this.phase = PHASE.SUCCESS;
          } else if (status === config.STATUS_WSP_FAILED) {
            const asyncCode = response.data.async_code;
            switch (asyncCode) {
              case error.WSP_CONDITION_ERROR:
                // 実施条件未成立表示画面に遷移
                this.phase = PHASE.CONDITION_ERROR;
                break;
              case error.WSP_DETECT_DTC_ERROR:
                // DTC検出エラーの場合
                this.phase = PHASE.DTC_DETECTION_ERROR;
                break;
              case error.WSP_ABORT_ERROR:
                // 異常終了画面に遷移
                this.phase = PHASE.ABORT_ERROR;
                break;
              default:
                this.showUnexpectedError();
            }
          } else {
            this.showUnexpectedError();
          }
        }
      } finally {
        this.isLoading = false;
      }
    },

    /**
     * 終了処理（実行監視時のキャンセル押下）
     * APIの結果はstandbyで取得する為ここでは取得を行わない
     */
    async stop() {
      try {
        this.isLoading = true;

        // キャンセルボタン押下不可のフラグ設定
        this.isDisabled = true;

        //バックエンドAPI POST,GET
        const token = session.getToken();
        await individualWorkSupportUtil.pollingWspApi(
          this, this.netAppId, this.systemId, this.workSupportId,
          {
            id_token: token.id_token,
            type: 'stop'
          },
          // エラー発生のコールバックを実装
          // ここでは利用していないが、引数の errorType にはあらかじめ用意された定数文字列が返ってくる。
          (errorType) => { errorType; this.showUnexpectedError(); },
          // POST API 結果が異常の場合（handler.validate でエラーが発生した場合）のコールバックを定義する（validateErrorResult の中は同様のため、省略する）
          (result) => { this.validateErrorResult(result); },
          // GET API 結果が異常の場合（handler.validate でエラーが発生した場合）のコールバックを定義する（validateErrorResult の中は同様のため、省略する）
          (result) => { this.validateErrorResult(result); },
        );
      } finally {
        this.isLoading = false;
      }
    },

    /** その他エラー発生時の表示 */
    showUnexpectedError() {
      // 異常終了：共通エラー表示
      this.phase = PHASE.FAILED;
    },
  },

  async mounted() {
    this.init();
  },
};